import type { ReactElement } from 'react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import type { TopicEvent, TopicMoment } from '@contracts/topics';
import { useTopic } from '../api/queries';
import { BackLink, Broken, Button, Card, Empty, Rows, SkeletonRows, Who } from '../ui';
import { KindTag } from '../meeting/Records';
import { Trend } from './Topics';
import { SameWork } from './SameWork';
import { RenameTopic } from './RenameTopic';
import { ago, dateLabel } from '../lib/time';

/**
 * One subject, across every meeting that turned on it (PRD 52).
 *
 * Three questions, in the order somebody asks them: **where does this stand**,
 * **how did it get here**, and **who is carrying it**. The timeline is the
 * record and the synthesis is a convenience — which is why the page holds
 * together with the synthesis missing, and says so rather than hiding the
 * section.
 *
 * ⚠ The timeline **points at** the ledger rather than copying it. A commitment
 * closed, a decision superseded, a risk that happened — each changes what this
 * page says without anybody rewriting it, and the two can never disagree.
 */
export function Topic(): ReactElement {
  const { id = '' } = useParams();
  const topic = useTopic(id);
  const [joining, setJoining] = useState(false);
  const [renaming, setRenaming] = useState(false);

  if (topic.isPending) {
    return (
      <div className="page stack-lg">
        <Card flush>
          <SkeletonRows rows={5} />
        </Card>
      </div>
    );
  }
  if (topic.isError) {
    return (
      <div className="page stack-lg">
        <Broken what="this topic" retry={() => void topic.refetch()} />
      </div>
    );
  }
  const t = topic.data;

  return (
    <div className="page stack-lg">
      <div className="stack" style={{ gap: 6 }}>
        <div className="toolbar">
          <BackLink to="/topics">Topics</BackLink>
        </div>
        <div className="page-head" style={{ alignItems: 'flex-start', marginBottom: 0 }}>
          <h1 className="t-title">{t.name}</h1>
          {/* ⚠ The linker's misses are a person's to mend: two threads that
              are one piece of work, each holding half of it. */}
          <div className="cluster" style={{ gap: 8 }}>
            <Button size="sm" onClick={() => setRenaming(true)}>
              Rename…
            </Button>
            <Button size="sm" onClick={() => setJoining(true)}>
              Same work as…
            </Button>
          </div>
        </div>
        {/* ⚠ Where it crossed, first. That is what makes it a topic rather
            than a series wearing a different hat. */}
        <p className="mono">
          {t.places} different {t.places === 1 ? 'place' : 'places'} · {t.mentions}{' '}
          {t.mentions === 1 ? 'meeting' : 'meetings'} · first raised {dateLabel(t.firstRaisedAt)} ·
          last {ago(t.lastMentionedAt)}
          {t.quiet ? ' · quiet lately' : ''}
        </p>
        <p className="mono topic-where-lead">{t.placeNames.join(' · ')}</p>
        {t.description ? <p className="t-body topic-what-lead">{t.description}</p> : null}
      </div>

      <div className="grid-2 aside-fluid">
        <div className="stack-lg">
          <Card
            title="Where it stands"
            aside={t.standsAt ? <span className="mono">as of {ago(t.standsAt)}</span> : null}
          >
            {t.stands ? (
              <div className="stack" style={{ gap: 8 }}>
                {t.stands
                  .split('\n')
                  .filter((l) => l.trim())
                  .map((line, i) => (
                    <p key={i} className="t-body topic-stands">
                      {line}
                    </p>
                  ))}
              </div>
            ) : (
              /* ⚠ Said, not hidden. A missing section reads as "there is
                 nothing to say" and as "the product did not look" at once. */
              <Empty lead="Not written up yet.">
                Where a subject stands is re-stated once it has moved enough to be worth saying. The
                timeline below is the record either way.
              </Empty>
            )}
          </Card>

          <Card title="How it got here" aside={<span className="mono">newest first</span>} flush>
            <div className="timeline">
              {t.timeline.map((moment) => (
                <Moment key={moment.meeting.id} moment={moment} />
              ))}
            </div>
          </Card>
        </div>

        <div className="stack-lg">
          <Card title="Activity">
            <div className="stack" style={{ gap: 10 }}>
              <Trend bars={t.trend} tall />
              <span className="mono">
                {t.trend.reduce((a, b) => a + b, 0)} in the last {t.weeks} weeks
              </span>
            </div>
          </Card>

          <Card title="Raised by" flush>
            <Rows>
              {t.raisedBy.map((p) => (
                <div key={p.name} className="row dense">
                  <span className="grow">
                    <Who email="" name={p.name} />
                  </span>
                  <span className="mono">{p.meetings}</span>
                </div>
              ))}
              {t.raisedBy.length === 0 ? (
                <div className="row dense">
                  <span className="t-secondary">Nobody was named in these meetings.</span>
                </div>
              ) : null}
            </Rows>
          </Card>

          <Card title="Meetings" flush>
            <Rows>
              {t.timeline.map((m) => (
                <Link key={m.meeting.id} to={`/m/${m.meeting.id}`} className="row dense-list">
                  <span className="row-title grow truncate">{m.meeting.title}</span>
                  <span className="mono">{dateLabel(m.meeting.startedAt)}</span>
                </Link>
              ))}
            </Rows>
          </Card>
        </div>
      </div>
      {joining ? (
        <SameWork topic={{ id: t.id, name: t.name }} onClose={() => setJoining(false)} />
      ) : null}
      {renaming ? (
        <RenameTopic
          id={t.id}
          name={t.name}
          description={t.description}
          onClose={() => setRenaming(false)}
        />
      ) : null}
    </div>
  );
}

/** One meeting's worth: what it did to the subject, and what came out of it. */
function Moment({ moment }: { moment: TopicMoment }): ReactElement {
  return (
    <div className="moment">
      <div className="moment-when">
        <span className="moment-dot" aria-hidden="true" />
        <span className="mono">{dateLabel(moment.meeting.startedAt)}</span>
      </div>
      <div className="moment-body">
        {moment.summary ? <p className="moment-said">{moment.summary}</p> : null}
        <Link to={`/m/${moment.meeting.id}`} className="mono moment-meeting">
          {moment.meeting.title}
        </Link>
        {moment.events.length > 0 ? (
          <div className="moment-events">
            {moment.events.map((e) => (
              <Event key={`${e.kind}-${e.id}`} event={e} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Event({ event }: { event: TopicEvent }): ReactElement {
  const gone = event.status !== 'OPEN' && event.status !== 'STANDS';
  return (
    <div className={`moment-event ${gone ? 'is-ended' : ''}`}>
      <KindTag kind={event.kind} />
      <div className="stack" style={{ gap: 2 }}>
        <span className="moment-event-text">{event.text}</span>
        {event.detail ? <span className="moment-event-detail">{event.detail}</span> : null}
      </div>
    </div>
  );
}
